import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { updateOverheadStatus } from '../api';
import { mapOverheadStatusToDTO } from '../mappers/overhead.mapper';
import { OverheadStatus } from '../types/overhead';
import { invalidateOverheadsQueries } from './use-overheads-invalidation';

/**
 * Flips a cost overhead between Planned and Paid.
 */
export const useUpdateOverheadStatus = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.overheads');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: ({ overheadId, status }: { overheadId: number; status: OverheadStatus }) =>
      updateOverheadStatus(overheadId, { status: mapOverheadStatusToDTO(status) }),
    onSuccess: async (_, { status }) => {
      // Close any owning modal first, then toast — a toast popping over a
      // still-open modal looks broken. Invalidation last: it awaits the
      // refetch and would delay both.
      onSuccess?.();
      displayToastSuccess(
        status === OverheadStatus.Paid
          ? t('toasts.markPaidSuccess')
          : t('toasts.markPlannedSuccess')
      );
      await invalidateOverheadsQueries(queryClient);
    },
    onError: error =>
      displayToastErrorFromApiWithFallback(error, 'authenticated.overheads:toasts.statusError'),
  });
};
